import { m } from "framer-motion";
import { Hammer, Shield, DollarSign, Users, Clock, Award } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";

const reasons = [
  {
    icon: Hammer,
    title: "One Crew, Every Trade",
    desc: `We self-perform ${services.length} trades — remodeling, painting, masonry, roofing, retaining walls, pavers, flooring and new construction — so you're not coordinating four contractors on one project.`,
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Registered New Jersey LLC",
    desc: "EC Design and Construction LLC is a registered New Jersey business operating out of Clifton. We're happy to put our documentation in your hands before work begins.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Free Written Estimates",
    desc: "We come out, look at the actual job, and give you the scope and price in writing before anything is scheduled — and 10% off your next project when you refer a customer.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: `Open ${siteConfig.hours.days}`,
    desc: `We're on the job ${siteConfig.hours.days.toLowerCase()}, ${siteConfig.hours.time}, and closed ${siteConfig.hours.closed}. Six working days a week means your project keeps moving.`,
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Owner-Led and Local",
    desc: `Based in ${siteConfig.address.city}, ${siteConfig.address.state} and working across ${siteConfig.serviceAreas.length} towns in Passaic, Bergen, Essex and Morris counties. The owner is on the job, not just on the estimate.`,
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "English & Spanish",
    desc: "Talk to us in whichever language you're comfortable with. Hablamos español — desde el presupuesto hasta el último día del proyecto.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why {siteConfig.name}
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons homeowners and property owners across northern New Jersey hand us their
            projects.
          </m.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <m.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-primary text-primary-foreground"
                  : "bg-primary/20 text-primary"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
